// InventoryValuation.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

const InventoryValuation = () => {
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [stockData, setStockData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productsList, setProductsList] = useState([]);

  const printRef = useRef();

  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) value = 0;
    const currencyCode = getCompanyCurrency(company);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currencyCode} ${Number(value).toFixed(2)}`;
    }
  }, [company]);

  const fetchCompany = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const res = await axios.get(`${url}/api/company/current`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      setCompany(res.data);
    } catch (err) {
      setError('Could not load company information. Please refresh or contact support.');
      setCompany(null);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${url}/api/products`, {
        headers: getAuthHeaders(),
        params: { status: 'active', limit: 500 },
        timeout: 5000,
      });
      setProductsList(res.data.data || []);
    } catch (err) {
      setProductsList([]);
    }
  }, []);

  const fetchValuation = useCallback(async () => {
    setLoadingReport(true);
    setError(null);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedProductId) params.productId = selectedProductId;

      const res = await axios.get(`${url}/api/inventorystock/reports/inventory-valuation`, {
        headers: getAuthHeaders(),
        params,
      });
      const data = res.data.data || [];
      setStockData(data);
      if (data.length === 0) {
        setError('No inventory records found for the selected filters.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report. Please try again.');
      setStockData([]);
    } finally {
      setLoadingReport(false);
    }
  }, [fromDate, toDate, selectedProductId]);

  useEffect(() => {
    fetchCompany();
    fetchProducts();
  }, [fetchCompany, fetchProducts]);

  const handleGenerate = () => fetchValuation();

  // ✅ Single print handler — uses the browser's native print with the
  //    .print-only block below. No more new-window duplication.
  const handlePrint = () => window.print();

  // ---- CSV Export ----
  const handleExportCSV = () => {
    if (stockData.length === 0) {
      alert('No data to export.');
      return;
    }
    const headers = [
      'No.', 'Warehouse', 'Category', 'Barcode', 'Item Name', 'UOM',
      'Qty', 'Unit Cost', 'Subtotal', 'Shipping + Other', 'General Total', 'Sale Price'
    ];
    const rows = stockData.map((item, idx) => {
      const subtotal = (item.quantity || 0) * (item.avg_cost || 0);
      const shippingOther = (item.shipping_cost || 0) + (item.other_charges || 0);
      const generalTotal = subtotal + shippingOther;
      return [
        idx + 1,
        item.warehouse_name || '—',
        item.category_name || '—',
        item.barcode || '—',
        item.product_name || '',
        item.uom_name || '—',
        item.quantity || 0,
        item.avg_cost || 0,
        subtotal,
        shippingOther,
        generalTotal,
        item.sale_price || 0
      ];
    });
    const grandTotalRow = [
      '', '', '', '', '', '', '', '', '', 'Grand Total:', grandTotal, ''
    ];
    const csvData = [headers, ...rows, grandTotalRow];
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inventory-valuation-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  // ---- End CSV Export ----

  const grandTotal = stockData.reduce((sum, item) => {
    const subtotal = (item.quantity || 0) * (item.avg_cost || 0);
    const shippingOther = (item.shipping_cost || 0) + (item.other_charges || 0);
    return sum + subtotal + shippingOther;
  }, 0);

  const primaryColor = '#2e3192';
  const secondaryColor = '#f1592a';

  const getLogoUrl = () => {
    if (!company?.logo) return null;
    if (company.logo.startsWith('http')) return company.logo;
    const cleanPath = company.logo.replace(/^\/+/, '');
    return `${url}/${cleanPath}`;
  };

  // ✅ Phone string (1, 2, 3) + fiscal year + currency for the A4 header
  const phoneLine = [
    company?.contact,
    company?.contact_2,
    company?.contact_3,
  ].filter(Boolean).join(' · ');

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const companyCurrency = getCompanyCurrency(company);

  const rangeLabel = (() => {
    if (fromDate && toDate) {
      return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
    }
    if (fromDate) return `From ${new Date(fromDate).toLocaleDateString()}`;
    if (toDate) return `Until ${new Date(toDate).toLocaleDateString()}`;
    return 'All Time';
  })();

  if (loadingCompany) {
    return <div className="flex justify-center items-center h-64" style={{ color: secondaryColor }}>Loading company data...</div>;
  }

  if (!company && !loadingCompany) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
        {error || 'Unable to load company information. Please log in again.'}
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '1.5rem' }}>
      {/* ==================================================
          SCREEN PREVIEW
      ================================================== */}
      <div className="screen-only" ref={printRef}>
        {/* HEADER */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '1.5rem', marginBottom: '1.5rem', borderLeft: `4px solid ${primaryColor}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {getLogoUrl() && (
                <img
                  src={getLogoUrl()}
                  alt="Company Logo"
                  style={{ height: '64px', width: '64px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #ddd' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: primaryColor }}>{company?.company_name || 'Company Name'}</h1>
                <p style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {company?.business_type?.name || 'Business'} | Code: {company?.company_code || 'N/A'}
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'right', color: '#495057', fontSize: '0.875rem' }}>
              <p>{company?.address || 'No address provided'}</p>
              <p style={{ marginTop: '0.25rem' }}>
                📞 {company?.contact || 'No phone'}
                {company?.contact_2 ? ` · ${company.contact_2}` : ''}
                {company?.contact_3 ? ` · ${company.contact_3}` : ''}
                <br />
                ✉️ {company?.admin_email || 'No email'}
              </p>
              <p style={{ fontSize: '0.75rem', color: '#adb5bd', marginTop: '0.25rem' }}>
                Fiscal year start: {company?.fiscal_year_start ? new Date(company.fiscal_year_start).toLocaleDateString() : 'Not set'}
              </p>
            </div>
          </div>
          <div style={{ marginTop: '1rem', paddingTop: '0.5rem', borderTop: '1px solid #e9ecef' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', textAlign: 'center', color: secondaryColor }}>Inventory Valuation Report</h2>
          </div>
        </div>

        {/* FILTERS (hidden when printing) */}
        <div className="no-print" style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: primaryColor, marginBottom: '0.25rem' }}>From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: '100%', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '0.5rem 0.75rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: primaryColor, marginBottom: '0.25rem' }}>To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: '100%', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '0.5rem 0.75rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: primaryColor, marginBottom: '0.25rem' }}>Product</label>
              <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} style={{ width: '100%', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', backgroundColor: 'white' }}>
                <option value="">— All Items —</option>
                {productsList.map((prod) => (
                  <option key={prod._id} value={prod._id}>{prod.product_code} - {prod.product_name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={handleGenerate} disabled={loadingReport} style={{ flex: 1, backgroundColor: primaryColor, color: 'white', fontWeight: '500', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: loadingReport ? 'not-allowed' : 'pointer', opacity: loadingReport ? 0.6 : 1 }}>{loadingReport ? 'Loading...' : 'Generate Report'}</button>
              <button onClick={handlePrint} disabled={stockData.length === 0} style={{ backgroundColor: secondaryColor, color: 'white', fontWeight: '500', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: stockData.length === 0 ? 'not-allowed' : 'pointer', opacity: stockData.length === 0 ? 0.6 : 1 }}>🖨️ Print</button>
              <button onClick={handleExportCSV} disabled={stockData.length === 0} style={{ backgroundColor: '#28a745', color: 'white', fontWeight: '500', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: stockData.length === 0 ? 'not-allowed' : 'pointer', opacity: stockData.length === 0 ? 0.6 : 1 }}>📥 CSV</button>
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.75rem', fontStyle: 'italic' }}>* Date range filters stock records created between From and To (based on inventory entry date).</div>
        </div>

        {/* REPORT TABLE */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          {error && <div style={{ backgroundColor: '#fff3f3', borderLeft: `4px solid ${primaryColor}`, padding: '1rem', margin: '1rem', borderRadius: '0.375rem' }}><p style={{ color: '#d32f2f', margin: 0 }}>{error}</p></div>}
          {loadingReport ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '16rem' }}><div style={{ border: `4px solid ${secondaryColor}`, borderTop: `4px solid ${primaryColor}`, borderRadius: '50%', width: '3rem', height: '3rem', animation: 'spin 1s linear infinite' }}></div></div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: primaryColor, color: 'white' }}>
                  <tr>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>No.</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Warehouse</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Category</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Barcode</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Item Name</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>UOM</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Unit Cost</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Subtotal</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Shipping + Other</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>General Total</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Sale Price</th>
                  </tr>
                </thead>
                <tbody>
                  {stockData.length === 0 ? (
                    <tr>
                      <td colSpan="12" style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>No inventory records found. Adjust filters and click Generate.</td>
                    </tr>
                  ) : (
                    stockData.map((item, idx) => {
                      const subtotal = (item.quantity || 0) * (item.avg_cost || 0);
                      const shippingOther = (item.shipping_cost || 0) + (item.other_charges || 0);
                      const generalTotal = subtotal + shippingOther;
                      return (
                        <tr key={item.stock_id || idx} style={{ borderBottom: '1px solid #f1f3f5' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef9e8'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: primaryColor }}>{idx + 1}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{item.warehouse_name || '—'}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{item.category_name || '—'}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{item.barcode || '—'}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontWeight: '500' }}>{item.product_name}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{item.uom_name || '—'}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>{item.quantity}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>{formatCurrency(item.avg_cost)}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>{formatCurrency(subtotal)}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>{formatCurrency(shippingOther)}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(generalTotal)}</td>
                          <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>{formatCurrency(item.sale_price)}</td>
                        </tr>
                      );
                    })
                  )}
                  {stockData.length > 0 && (
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="10" style={{ padding: '0.75rem 1rem', textAlign: 'right', color: primaryColor }}>Grand Total:</td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: secondaryColor }}>{formatCurrency(grandTotal)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="hide-on-print" style={{ fontSize: '0.75rem', textAlign: 'center', color: '#adb5bd', marginTop: '1.5rem' }}>
          Valuation based on average cost (AVCO). Data as of {new Date().toLocaleString()}
        </div>
      </div>

      {/* ==================================================
          PRINT-ONLY A4 REPORT
      ================================================== */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {getLogoUrl() && (
              <img
                src={getLogoUrl()}
                alt="Logo"
                className="print-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">
                {company?.company_name || 'Company Name'}
              </h1>

              {/* ✅ Address */}
              <p className="print-company-line">
                {company?.address || 'No address'}
              </p>

              {/* ✅ Phone 1 · Phone 2 · Phone 3 */}
              {phoneLine && (
                <p className="print-company-line">Tel: {phoneLine}</p>
              )}

              <p className="print-company-line">
                {company?.admin_email ? `Email: ${company.admin_email}` : ''}
                {company?.company_code ? ` · Code: ${company.company_code}` : ''}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">{fiscalYearLabel}-{fiscalYearLabel + 1}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="print-title-block">
          <h2 className="print-report-title">INVENTORY VALUATION</h2>
          <p className="print-report-subtitle">
            {rangeLabel} · {stockData.length} item(s)
          </p>
        </div>

        {/* Print table */}
        <table className="print-table">
          <thead>
            <tr>
              <th className="print-col-no">No.</th>
              <th>Warehouse</th>
              <th>Category</th>
              <th>Item Name</th>
              <th className="print-col-qty">Qty</th>
              <th className="print-col-money">Unit Cost</th>
              <th className="print-col-money">Subtotal</th>
              <th className="print-col-money">Shipping + Other</th>
              <th className="print-col-money">General Total</th>
              <th className="print-col-money">Sale Price</th>
            </tr>
          </thead>
          <tbody>
            {stockData.length === 0 ? (
              <tr>
                <td colSpan="10" className="print-empty">
                  No inventory records found for the selected filters.
                </td>
              </tr>
            ) : (
              stockData.map((item, idx) => {
                const subtotal = (item.quantity || 0) * (item.avg_cost || 0);
                const shippingOther = (item.shipping_cost || 0) + (item.other_charges || 0);
                const generalTotal = subtotal + shippingOther;
                return (
                  <tr key={item.stock_id || `p-${idx}`}>
                    <td className="print-col-no">{idx + 1}</td>
                    <td>{item.warehouse_name || '—'}</td>
                    <td>{item.category_name || '—'}</td>
                    <td>{item.product_name}</td>
                    <td className="print-col-qty">{item.quantity}</td>
                    <td className="print-col-money">{formatCurrency(item.avg_cost)}</td>
                    <td className="print-col-money">{formatCurrency(subtotal)}</td>
                    <td className="print-col-money">{formatCurrency(shippingOther)}</td>
                    <td className="print-col-money">{formatCurrency(generalTotal)}</td>
                    <td className="print-col-money">{formatCurrency(item.sale_price)}</td>
                  </tr>
                );
              })
            )}
            {stockData.length > 0 && (
              <tr className="print-total-row">
                <td colSpan="8" className="print-total-label">
                  GRAND TOTAL VALUE
                </td>
                <td className="print-col-money print-total-value">
                  {formatCurrency(grandTotal)}
                </td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Signature footer */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      {/* ==================================================
          INLINE PRINT STYLES
      ================================================== */}
      <style>{`
        .print-only { display: none; }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @page {
          size: A4 portrait;
          margin: 12mm 14mm 14mm 14mm;
        }

        @media print {
          html, body {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          /* Hide EVERYTHING (sidebar, menu, search, filters) */
          body * { visibility: hidden !important; }

          /* Show only the print block */
          .print-only,
          .print-only * { visibility: visible !important; }

          .print-only {
            position: absolute !important;
            left: 0; top: 0;
            width: 100%;
            display: block !important;
            padding: 0; margin: 0;
            background: #fff !important;
          }

          .screen-only,
          .no-print,
          .hide-on-print { display: none !important; }

          /* ---------- PRINT BLOCK LAYOUT ---------- */
          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 4mm;
            border-bottom: 1.5pt solid #222;
            margin-bottom: 5mm;
          }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 18mm; max-width: 26mm; object-fit: contain; }
          .print-company-name { font-size: 14pt; font-weight: 700; color: #170f48; margin: 0 0 1mm 0; }
          .print-company-line { font-size: 8.5pt; color: #444; margin: 0.4mm 0; }
          .print-header-right { text-align: right; font-size: 8.5pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 1mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 26mm; text-align: right; color: #111; }

          .print-title-block { text-align: center; margin-bottom: 5mm; }
          .print-report-title { font-size: 14pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 1mm 0; }
          .print-report-subtitle { font-size: 9.5pt; color: #555; margin: 0; }

          /* ---------- PRINT TABLE ---------- */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8.5pt;
            page-break-inside: auto;
          }
          .print-table thead { display: table-header-group; }
          .print-table tr { page-break-inside: avoid; }
          .print-table th,
          .print-table td {
            border: 0.5pt solid #999;
            padding: 3pt 4pt;
            vertical-align: middle;
          }
          .print-table thead th {
            background: #eeeeee !important;
            color: #000 !important;
            font-weight: 700;
            text-align: left;
            font-size: 8pt;
            text-transform: uppercase;
            letter-spacing: 0.2pt;
          }
          .print-col-no    { width: 8mm; text-align: center; }
          .print-col-qty   { width: 14mm; text-align: right; }
          .print-col-money {
            width: 22mm;
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }

          .print-total-row { background: #f4f4f4 !important; font-weight: 700; }
          .print-total-label { text-align: right; letter-spacing: 0.5pt; }
          .print-total-value { font-weight: 700; }
          .print-empty { text-align: center; padding: 12pt; font-style: italic; }

          /* ---------- PRINT FOOTER ---------- */
          .print-footer { margin-top: 10mm; }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 5mm;
          }
          .print-signature { flex: 1; text-align: center; font-size: 8.5pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7.5pt; color: #888; margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default InventoryValuation;
